import React, { useEffect, useState } from 'react';
import { obterDefesa, confirmarRealizacao } from '../../services/defesaService';
import { useParams, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Button
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

// Importamos o componente ArquivarMonografia
import ArquivarMonografia from '../../components/Monografia/ArquivarMonografia';
import { obterMonografia } from '../../services/monografiaService'; // Para obter os detalhes da monografia

const DefesaDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();


   // DEBUGGING - adicione estas linhas temporariamente
   console.log('URL params:', useParams());
   console.log('ID extraído:', id);
   console.log('URL atual:', window.location.pathname);
   
  const { user } = useAuth();
  const [defesa, setDefesa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [monografia, setMonografia] = useState(null);
  const [avaliacaoExistente, setAvaliacaoExistente] = useState(false);

  useEffect(() => {
    const fetchDefesa = async () => {
      try {
        setLoading(true);
        const response = await obterDefesa(id);
        setDefesa(response.data);
        setError(null);
        
        // Se a defesa estiver realizada, buscar monografia e verificar avaliação existente
        if (response.data.status === 'REALIZADA') {
          if (response.data.monografia_id) {
            await carregarMonografia(response.data.monografia_id);
          }
          try {
            await api.get(`/avaliacoes/defesa/${response.data.id}`);
            setAvaliacaoExistente(true);
          } catch {
            setAvaliacaoExistente(false);
          }
        }
      } catch (err) {
        console.error('Erro ao carregar defesa:', err);
        setError('Não foi possível carregar os detalhes da defesa.');
      } finally {
        setLoading(false);
      }
    };

    fetchDefesa();
  }, [id]);

  const carregarMonografia = async (monografiaId) => {
    try {
      const response = await obterMonografia(monografiaId);
      setMonografia(response.data);
    } catch (err) {
      console.error('Erro ao carregar monografia:', err);
    }
  };

  const handleConfirmarRealizacao = async () => {
    try {
      setConfirming(true);
      await confirmarRealizacao(id);
      // Recarrega os dados da defesa
      const response = await obterDefesa(id);
      setDefesa(response.data);
      
      // Se a defesa foi confirmada como realizada, carregar monografia
      if (response.data.status === 'REALIZADA' && response.data.monografia_id) {
        await carregarMonografia(response.data.monografia_id);
      }
    } catch (err) {
      setError('Erro ao confirmar realização da defesa.');
      console.error('Erro:', err);
    } finally {
      setConfirming(false);
    }
  };

  const handleMonografiaArquivada = () => {
    // Recarregar os dados após arquivamento
    if (defesa?.monografia_id) {
      carregarMonografia(defesa.monografia_id);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!defesa) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Defesa não encontrada.
      </Alert>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Detalhes da Defesa
      </Typography>

      <Card>
        <CardContent>
          {/* Cabeçalho: Local e Status */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Typography variant="h5">
              Local: {defesa.local_defesa}
            </Typography>
            <Chip
              label={defesa.status}
              color={
                defesa.status === 'AGENDADA'
                  ? 'info'
                  : defesa.status === 'REALIZADA'
                  ? 'warning'
                  : 'default'
              }
              sx={{ fontWeight: 'medium' }}
            />
          </Box>

          {/* Informações Básicas */}
          <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon color="action" />
              <Typography variant="body1">
                <strong>Data e Hora:</strong>{' '}
                {new Date(defesa.data_defesa).toLocaleString()}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Dados do júri */}
          <Box sx={{ display: 'grid', gap: 2, mb: 3 }}>
            {defesa.presidente?.usuario && (
              <Typography variant="body1">
                <strong>Presidente do Júri:</strong> {defesa.presidente.usuario.nome}
                {defesa.presidente.departamento
                  ? ` - ${defesa.presidente.departamento}`
                  : ''}
              </Typography>
            )}
            {defesa.arguente?.usuario && (
              <Typography variant="body1">
                <strong>Arguente:</strong> {defesa.arguente.usuario.nome}
              </Typography>
            )}
          </Box>

          {/* Botões de Ação */}
          <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
            {/* Botão de Confirmar Realização: só aparece se o usuário for presidente, 
                o status for AGENDADA e o presidente for o usuário logado */}
            {user?.tipo === 'presidente' &&
              defesa.status === 'AGENDADA' &&
              defesa.presidente?.usuario?.id === user.id && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleConfirmarRealizacao}
                  disabled={confirming}
                >
                  {confirming ? 'Confirmando...' : 'Confirmar Realização'}
                </Button>
              )}

            {/* Botão de Arquivar no Repositório */}
            {defesa.status === 'REALIZADA' && monografia &&
              (user?.tipo === 'cientifico' ||
               user?.tipo === 'instituicao' ||
               user?.id === monografia.projeto?.supervisor?.usuario_id) && (
              <ArquivarMonografia 
                monografia={monografia} 
                onSuccess={handleMonografiaArquivada} 
              />
            )}

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
            >
              Voltar
            </Button>

            {defesa.status === 'REALIZADA' &&
              ['arguente', 'presidente', 'supervisor'].includes(user?.tipo) &&
              !avaliacaoExistente && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/avaliacoes/registrar?defesaId=${defesa.id}`)}
              >
                Avaliar Defesa
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DefesaDetails;